import { Box, Typography, Button, FormHelperText, Grid } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { TFormValues } from '../types.ts';
import { contrAgentType } from '~/constants/directoryList.ts';
import { styles } from './styles.ts';
import { FormPhoneField } from '~/components/FormComponents/FormPhoneField.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { SystemStyleObject, Theme } from '@mui/system';

export const ManagerFormContext = () => {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useFormContext<TFormValues>();

  const financialServices = useWatch({ control, name: 'financialServices' });

  const handleFinancialServicesChange = (isChecked: boolean) => {
    if (isChecked) {
      clearErrors('atLeastOneCheckbox');
      return;
    }

    setValue('financialServicesClientType', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handlePurposeCheckboxChange = (isChecked: boolean) => {
    if (isChecked) {
      clearErrors('atLeastOneCheckbox');
    }
  };

  const showAtLeastOneCheckboxError = !!errors.atLeastOneCheckbox;

  return (
    <Box sx={styles.container} data-testid="manager-form-context">
      <Grid
        container
        columnSpacing={2.5}
        rowSpacing={{ xs: 1.5, sm: 1 }}
        sx={[styles.fieldsWrapper as SystemStyleObject<Theme>, { alignItems: 'flex-start' }]}
      >
        <Grid size={{ xs: 12, sm: 6 }} sx={styles.fieldsWrapperItem}>
          <Box sx={styles.phoneFieldWrapper}>
            <FormPhoneField
              name="phone"
              label="Номер контактного телефону"
              dataTestId="phone-field"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} sx={styles.fieldsWrapperItem}>
          <FormInput
            fullWidth
            name="email"
            label="Адреса електронної пошти"
            placeholder="Введіть адресу електронної пошти"
            inputProps={{
              'data-testid': 'text-field',
            }}
          />
        </Grid>
      </Grid>

      <Box sx={styles.checkboxWrapper}>
        <Typography sx={styles.purposeText} data-testid="purposeText">
          З якою метою надається згода:
        </Typography>

        <FormCheckbox
          name="financialServices"
          label="Надання Банком фінансових послуг"
          onChange={handleFinancialServicesChange}
          formControlLabelProps={{
            sx: styles.checkboxLabel,
            'data-testid': 'financialServicesCheckbox',
          }}
        />

        {financialServices && (
          <FormAutocomplete
            name="financialServicesClientType"
            options={contrAgentType}
            label="Тип клієнта"
            inputProps={{
              placeholder: 'Оберіть тип клієнта',
              'data-testid': 'select-field-financialServicesClientType',
            }}
            sx={styles.conditionalField}
          />
        )}

        <FormCheckbox
          name="cardAccess"
          label="Отримання карти – перепустки"
          onChange={handlePurposeCheckboxChange}
          formControlLabelProps={{
            sx: styles.checkboxLabel,
            'data-testid': 'cardAccessCheckbox',
          }}
        />

        <FormCheckbox
          name="businessRelationship"
          label="Встановлення (підтримання) ділових (договірних) відносин"
          onChange={handlePurposeCheckboxChange}
          formControlLabelProps={{
            sx: styles.checkboxLabel,
            'data-testid': 'businessRelationshipCheckbox',
          }}
        />

        <FormCheckbox
          name="creditBureauData"
          label="Надання доступу до кредитної історії"
          onChange={handlePurposeCheckboxChange}
          formControlLabelProps={{
            sx: styles.checkboxLabel,
            'data-testid': 'creditBureauDataCheckbox',
          }}
        />

        {showAtLeastOneCheckboxError && (
          <FormHelperText error data-testid="atLeastOneCheckboxError" sx={styles.errorText}>
            {errors.atLeastOneCheckbox?.message as string}
          </FormHelperText>
        )}
      </Box>

      <Button
        variant="contained"
        size="large"
        type="submit"
        disabled={isSubmitting}
        data-testid="submit-button"
      >
        Відправити
      </Button>
    </Box>
  );
};
